/**
 * ClaimLeftSidebarContent
 *
 * Tab-switcher for the claim detail left sidebar. Provides a SegmentedControl
 * to toggle between "Patient" (PatientInformationView) and "Billing Codes"
 * (reuses BillingCodesSection + SessionDetailsSection from SessionBillingPopover).
 *
 * The billing tab lets administrators edit the working billing claim on the
 * encounter, then rebuild the claim from that data.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import {
  useDebouncedWorkingBillingClaim,
  useGetEncounterQuery,
  useUpdateEncounterMutation,
} from '@features/encounters';
import { BillingCodesSection } from '@features/encounters/components/SessionBillingPopover/BillingCodesSection';
import { SessionDetailsSection } from '@features/encounters/components/SessionBillingPopover/SessionDetailsSection';
import type { Modality } from '@features/encounters/types/attestations';
import {
  bareCodesToLineItems,
  buildDiagnosisCodeSources,
  extractBillingCodes,
  extractDiagnosisCodes,
  extractPlaceOfService,
} from '@features/encounters/utils/billingClaim';
import { Box, Skeleton, Stack, Typography } from '@mui/material';

import PatientInformationView from '../../../../components/Patient/PatientInformationView';

import { ClaimRebuildButton } from './ClaimRebuildButton';

type SidebarTab = 'patient' | 'billing';

interface ClaimLeftSidebarContentProps {
  patientId?: string | null;
  encounterId?: string;
  claimId: string;
  claimStatus?: string;
  onRebuildStart?: () => void;
}

export const ClaimLeftSidebarContent: React.FC<ClaimLeftSidebarContentProps> = ({
  patientId,
  encounterId,
  claimId,
  claimStatus,
  onRebuildStart,
}) => {
  const hasPatient = Boolean(patientId);

  // Tab options — hide Patient tab when no patient ID
  const tabOptions = useMemo(() => {
    const options = [];
    if (hasPatient) {
      options.push({ value: 'patient', label: 'Patient' });
    }
    options.push({ value: 'billing', label: 'Billing Codes' });
    return options;
  }, [hasPatient]);

  const [activeTab, setActiveTab] = useState<SidebarTab>(hasPatient ? 'patient' : 'billing');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab switcher — only show if both tabs are available */}
      {tabOptions.length > 1 && (
        <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
          <SegmentedControl
            options={tabOptions}
            value={activeTab}
            onChange={(val) => setActiveTab(val as SidebarTab)}
            size="small"
          />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'patient' && patientId && (
          <PatientInformationView patientId={patientId} />
        )}
        {activeTab === 'billing' && (
          <BillingTabContent
            encounterId={encounterId}
            claimId={claimId}
            claimStatus={claimStatus}
            onRebuildStart={onRebuildStart}
          />
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Billing Tab — uses the same sub-components as SessionBillingPopover
// ---------------------------------------------------------------------------

interface BillingTabContentProps {
  encounterId?: string;
  claimId: string;
  claimStatus?: string;
  onRebuildStart?: () => void;
}

const BillingTabContent: React.FC<BillingTabContentProps> = ({
  encounterId,
  claimId,
  claimStatus,
  onRebuildStart,
}) => {
  const { data: encounterData, isLoading } = useGetEncounterQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  const [updateEncounter] = useUpdateEncounterMutation();

  const { update: updateBillingClaim } = useDebouncedWorkingBillingClaim({
    encounterId,
  });

  // Extract current values from encounter's working billing claim
  const cptCodes = useMemo(
    () => extractBillingCodes(encounterData?.working_billing_claim),
    [encounterData?.working_billing_claim],
  );

  const icd10Codes = useMemo(
    () => extractDiagnosisCodes(encounterData?.working_billing_claim),
    [encounterData?.working_billing_claim],
  );

  const placeOfService = useMemo(
    () => extractPlaceOfService(encounterData?.working_billing_claim),
    [encounterData?.working_billing_claim],
  );

  const modality = (encounterData?.modality as Modality | null) ?? null;

  const duration = useMemo(() => {
    if (encounterData?.start_time && encounterData?.end_time) {
      return Math.floor(
        (new Date(encounterData.end_time).getTime() -
          new Date(encounterData.start_time).getTime()) /
          (1000 * 60),
      );
    }
    return null;
  }, [encounterData?.start_time, encounterData?.end_time]);

  // Handlers — same pattern as NoteChatView
  const handleModalityChange = useCallback(
    async (newModality: Modality) => {
      if (!encounterId) return;
      const pos = newModality === 'in_person' ? '11' : newModality === 'video' ? '10' : '02';
      await updateEncounter({
        encounterId,
        encounterUpdate: { modality: newModality },
      });
      updateBillingClaim({ placeOfService: pos });
    },
    [encounterId, updateEncounter, updateBillingClaim],
  );

  const handlePlaceOfServiceChange = useCallback(
    (pos: string) => {
      updateBillingClaim({ placeOfService: pos });
    },
    [updateBillingClaim],
  );

  const handleDurationChange = useCallback(
    async (minutes: number) => {
      if (!encounterId) return;
      const startTime = encounterData?.start_time;
      if (startTime) {
        const endTime = new Date(
          new Date(startTime).getTime() + minutes * 60 * 1000,
        ).toISOString();
        await updateEncounter({
          encounterId,
          encounterUpdate: { end_time: endTime },
        });
      }
    },
    [encounterId, encounterData?.start_time, updateEncounter],
  );

  const handleCptCodesChange = useCallback(
    (codes: string[]) => {
      const claim = encounterData?.working_billing_claim;
      updateBillingClaim({
        lineItems: bareCodesToLineItems(codes, claim?.line_items ?? undefined),
      });
    },
    [encounterData?.working_billing_claim, updateBillingClaim],
  );

  const handleIcd10CodesChange = useCallback(
    (codes: string[]) => {
      const filtered = codes.filter(c => c && c.trim() !== '');
      const claim = encounterData?.working_billing_claim;
      updateBillingClaim({
        diagnosisCodes: filtered,
        diagnosisCodeSources: buildDiagnosisCodeSources(
          filtered,
          claim?.diagnosis_code_sources,
        ),
      });
    },
    [encounterData?.working_billing_claim, updateBillingClaim],
  );

  // No encounter linked
  if (!encounterId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.disabled">
          No encounter linked to this claim.
        </Typography>
      </Box>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={80} />
        </Stack>
      </Box>
    );
  }

  const showRebuild = claimStatus === 'draft' || claimStatus === 'ready';

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        {/* Session Details — modality, place of service, duration */}
        <Box>
          <Typography
            variant="overline"
            sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', mb: 1, display: 'block' }}
          >
            Session Details
          </Typography>
          <SessionDetailsSection
            modality={modality}
            onModalityChange={handleModalityChange}
            placeOfService={placeOfService}
            onPlaceOfServiceChange={handlePlaceOfServiceChange}
            duration={duration}
            onDurationChange={handleDurationChange}
            editable
          />
        </Box>

        {/* Billing Codes — CPT + ICD-10 */}
        <Box>
          <Typography
            variant="overline"
            sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', mb: 1, display: 'block' }}
          >
            Billing Codes
          </Typography>
          <BillingCodesSection
            cptCodes={cptCodes}
            onCptCodesChange={handleCptCodesChange}
            icd10Codes={icd10Codes}
            onIcd10CodesChange={handleIcd10CodesChange}
            editable
          />
        </Box>

        {/* Rebuild Button */}
        {showRebuild && (
          <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <ClaimRebuildButton
              claimId={claimId}
              claimStatus={claimStatus}
              onRebuildStart={onRebuildStart}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};
